import { computed, inject } from '@angular/core';
import {
  patchState,
  signalStore,
  type,
  withComputed,
  withProps,
} from '@ngrx/signals';
import {
  withEntities,
  entityConfig,
  EntityId,
  setEntity,
  updateEntity,
  removeEntity,
} from '@ngrx/signals/entities';
import {
  withCallStatus,
  withEntitiesLoadingCall,
  withCalls,
  withEntitiesCalls,
  callConfig,
  entityCallConfig,
  withLogger,
  withEntitiesLocalSort,
  withEntitiesMultiSelection,
  withEntitiesLocalPagination,
} from '@ngrx-traits/signals';
import {
  TextCombination,
  TextCombinationView,
  toPatchOperations,
} from '../models/text-combination.model';
import { TextCombinationService } from '../data/text-combination.service';
import { map, filter, throwError } from 'rxjs';
import { ToastService } from '@core/toast';
import { ConfirmDeleteDialogService } from '@core/confirmation';
import { EnumStore } from '@app/features/options/enum';
import { DamageCategory, GdvDamageKind } from 'src/app/models/enum-types.model';

const textCombinationConfig = entityConfig({
  entity: type<TextCombination>(),
  collection: 'textCombination',
  selectId: (combination: TextCombination) => combination.id,
});

export const TextCombinationStore = signalStore(
  withEntities(textCombinationConfig),
  withCallStatus({ collection: textCombinationConfig.collection }),
  withEntitiesLocalSort({
    entity: type<TextCombination>(),
    collection: textCombinationConfig.collection,
    defaultSort: { field: 'name', direction: 'asc' },
  }),
  withEntitiesMultiSelection({
    entity: type<TextCombination>(),
    collection: textCombinationConfig.collection,
  }),
  withEntitiesLocalPagination({
    entity: type<TextCombination>(),
    collection: textCombinationConfig.collection,
    pageSize: 20,
  }),
  withLogger({
    name: 'TextCombinationStore',
    showDiff: true,
  }),
  withProps(() => ({
    dialogService: inject(ConfirmDeleteDialogService),
    textCombinationService: inject(TextCombinationService),
    _toastService: inject(ToastService),
    enumStore: inject(EnumStore),
  })),
  withComputed(({ ...store }) => ({
    damageTypeEnums: computed<DamageCategory[]>(() =>
      store.enumStore.enumForKey<DamageCategory>('DamageCategory')
    ),
    gdvDamageKindEnums: computed<GdvDamageKind[]>(() =>
      store.enumStore.enumForKey<GdvDamageKind>('GdvDamageKind')
    ),
  })),
  withEntitiesLoadingCall({
    collection: textCombinationConfig.collection,
    fetchEntities: ({ textCombinationService }) => {
      return textCombinationService.loadList();
    },
  }),
  withComputed(({ textCombinationIds, textCombinationEntities }) => ({
    isEmpty: computed(() => textCombinationIds()?.length === 0),
    totalCount: computed(() => textCombinationIds()?.length || 0),
    totalTextCount: computed(() => {
      const entities = textCombinationEntities();
      return entities.reduce((sum, c) => sum + c.textCount, 0);
    }),
  })),
  withEntitiesCalls({
    ...textCombinationConfig,
    calls: ({ textCombinationService, ...store }) => ({
      updateCombination: entityCallConfig({
        paramsSelectId: (update) => String(update.id),
        call: (update: { id: EntityId; changes: Partial<TextCombination> }) =>
          textCombinationService
            .update(String(update.id), toPatchOperations(update.changes))
            .pipe(map(() => update)),
        mapError: () => 'Fehler beim Aktualisieren der Textkombination',
        onSuccess: (update) => {
          patchState(
            store,
            updateEntity(
              {
                id: String(update.id),
                changes: update.changes,
              },
              textCombinationConfig
            )
          );
        },
      }),
      deleteCombination: entityCallConfig({
        call: (id: EntityId) =>
          textCombinationService.delete(String(id)).pipe(map(() => id)),
        mapError: () => 'Fehler beim Löschen der Textkombination',
        onSuccess: (id) => {
          patchState(store, removeEntity(id, textCombinationConfig));
          store._toastService.success('Textkombination gelöscht', '');
        },
      }),
    }),
  }),
  withComputed(
    ({
      textCombinationEntitiesCurrentPage,
      damageTypeEnums,
      gdvDamageKindEnums,
    }) => ({
      textCombinationViews: computed<TextCombinationView[]>(() => {
        const entities = textCombinationEntitiesCurrentPage().entities ?? [];
        const damageTypes = damageTypeEnums() ?? [];
        const gdvKinds = gdvDamageKindEnums() ?? [];

        return entities
          .filter((e): e is TextCombination => !!e)
          .map((entity) => ({
            ...entity,
            damageCategoryDescription: damageTypes.find(
              (d) => d.id === entity.damageCategoryId
            )?.description,
            gdvDamageKindDescription: gdvKinds.find(
              (g) => g.id === entity.gdvDamageKindId
            )?.description,
          }));
      }),
    })
  ),
  withCalls(
    ({
      dialogService,
      textCombinationService,
      deleteCombination,
      _toastService,
      ...state
    }) => ({
      createCombination: callConfig({
        call: (combination: Partial<TextCombination>) =>
          textCombinationService.create(combination),
        mapError: () => 'Fehler beim Erstellen der Textkombination',
        onSuccess: (combination) => {
          patchState(
            state,
            setEntity(combination, {
              collection: textCombinationConfig.collection,
            })
          );
          _toastService.success('Textkombination angelegt', '');
        },
      }),
      requestDeleteDialog: callConfig({
        call: (combinationId: EntityId) =>
          dialogService
            .confirmDelete(
              'Sind Sie sicher, dass Sie diese Textkombination löschen möchten? Diese Aktion kann nicht rückgängig gemacht werden.'
            )
            .pipe(
              filter((confirmed) => confirmed),
              map(() => combinationId)
            ),
        onSuccess: (combinationId) => deleteCombination(combinationId),
      }),
      requestDeleteSelectedDialog: callConfig({
        call: () => {
          const selectedIds = state.textCombinationIdsSelected();
          if (selectedIds.length === 0) {
            return throwError(() => new Error('No combinations selected'));
          }
          return dialogService.confirmDelete().pipe(
            filter((confirmed) => confirmed),
            map(() => selectedIds.map((x) => parseInt(x.toString())))
          );
        },
        onSuccess: (selectedIds) => {
          selectedIds.forEach((id: EntityId) => deleteCombination(id));
          patchState(state, { textCombinationIdsSelectedMap: {} });
        },
      }),
    })
  )
);
